/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type {
  ItemHoverDetail,
  ItemFocusDetail,
  ItemInfo,
  PictoChartItemData
} from './pictoChart.type';

/**
 * Returns the item based on item info in a picto chart.
 * @param itemInfo
 * @param items
 * @returns
 */
function getItem<K, D extends PictoChartItemData<K>>(itemInfo: ItemInfo, items: D[]) {
  const { idx } = itemInfo;
  return items[idx];
}

/**
 * Returns the next item's info in a picto chart.
 * @param itemInfo
 * @param items
 * @returns
 */
function getNextItemInfo<K, D extends PictoChartItemData<K>>(itemInfo: ItemInfo, items: D[], isBottomLayoutOrigin: boolean
) {
  let idx = itemInfo.idx;

  idx = isBottomLayoutOrigin ? Math.max(0, idx - 1) : Math.min(items.length - 1, idx + 1);
  return { idx: idx };
}

/**
 * Returns the previous item's info in a picto chart.
 * @param itemInfo
 * @returns
 */
function getPrevItemInfo<K, D extends PictoChartItemData<K>>(itemInfo: ItemInfo, items: D[], isBottomLayoutOrigin: boolean) {
  let idx = itemInfo.idx;

  idx = isBottomLayoutOrigin ? Math.min(items.length - 1, idx + 1) : Math.max(0, idx - 1);
  return { idx: idx };
}

/**
 * Returns utils functions for navigation in a picto chart.
 * @param items
 * @returns
 */
export function getPictoChartNavUtils<K, D extends PictoChartItemData<K>>(items: D[], isBottomLayoutOrigin: boolean) {
  return {
    getItem: (itemInfo: ItemInfo) => {
      return getItem(itemInfo, items);
    },
    getDetailFromInfo: (itemInfo?: ItemInfo): ItemHoverDetail<K, D> | ItemFocusDetail<K, D> => {
      if (!itemInfo) {
        return { id: undefined, data: undefined };
      }
      const item = getItem(itemInfo, items);
      return { id: item ? item.id : undefined, data: item };
    },
    getPrevItemInfo: (itemInfo: ItemInfo) => {
      return getPrevItemInfo(itemInfo, items, isBottomLayoutOrigin);
    },
    getNextItemInfo: (itemInfo: ItemInfo) => {
      return getNextItemInfo(itemInfo, items, isBottomLayoutOrigin);
    }
  };
}
